/*
 * Jira Ticket:
 * Created Date: Wed, 3rd Jun 2020, 16:18:31 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect, Fragment } from 'react';
import {
  DateInput,
  FormDataConsumer,
  ImageField,
  ImageInput,
  Labeled,
  NumberInput,
  required,
  ReferenceArrayInput,
  SelectArrayInput,
  SelectInput,
  TextInput,
  useNotify,
  useTranslate,
} from 'react-admin';
import { DateInput as ReactAdminDateInput } from 'react-admin-date-inputs';
import { useForm } from 'react-final-form';
import { InputAdornment } from '@material-ui/core';
import { PreviewImageField } from 'Components/Fields';
import { columnStyles } from 'styles';
import {
  isValidUrl,
  nonNegativeNonZeroInt,
  validateLiveDate,
  validateEndDate,
} from 'utils/validation';
import { adStatusOptions } from 'utils/choices';
import { onDropRejected, onDrop } from 'utils';

const maxFileSize = 10485760; // 10MB in bytes
const allowedFileTypes = 'image/jpg, image/jpeg, image/png';
const aspectRatio = 0.67;

const liveDateValidation = [required(), validateLiveDate];
const endDateValidation = [required(), validateEndDate];
const urlValidation = [required(), url => isValidUrl(url)];
const freqWeightValidation = [
  required(),
  chance => nonNegativeNonZeroInt(chance),
];

const AspectRatioValidatingImageField = ({
  imgLoaded,
  resetImageField,
  ...props
}) => {
  const {
    record: { rawFile },
  } = props;
  var calcAspectRatio = null;
  if (rawFile && rawFile.width && rawFile.height) {
    calcAspectRatio =
      Math.round(
        (Number(rawFile.width / rawFile.height) + Number.EPSILON) * 100
      ) / 100;
  }
  useEffect(() => {
    if (calcAspectRatio && calcAspectRatio !== aspectRatio) {
      resetImageField();
    }
  }, [resetImageField, calcAspectRatio]);

  return (
    <Fragment>
      {imgLoaded && calcAspectRatio && calcAspectRatio === aspectRatio ? (
        <ImageField source="undefined" title="img" {...props} />
      ) : null}
    </Fragment>
  );
};

const SanitizedForm = ({
  classes,
  notify,
  urlValidation,
  freqWeightValidation,
  translate,
  setImgLoaded,
  imgLoaded,
  record,
}) => {
  const form = useForm();
  const resetImageField = () => {
    // reset the adImage field if the aspect ratio is not correct
    notify('error.advert.wrongAspectRatio', 'warning');
    return form.change('adImage', null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          label="resources.Advert.fields.adName"
          validate={required()}
        />
        <TextInput
          source="advertiserName"
          label="resources.Advert.fields.advertiserName"
          validate={required()}
        />
        <TextInput
          source="url"
          label="resources.Advert.fields.url"
          type="url"
          validate={urlValidation}
        />
        <ReferenceArrayInput
          label="resources.Advert.fields.regions"
          source="regionsIds"
          resource="Advert"
          reference="Region"
          perPage={1000}
          validate={required()}
          sort={{ field: 'name', order: 'ASC' }}
        >
          <SelectArrayInput
            source="id"
            optionText="name"
            label="resources.Advert.fields.regionsRequired"
          />
        </ReferenceArrayInput>
        <NumberInput
          source="duration"
          InputProps={{
            endAdornment: <InputAdornment position="end">ms</InputAdornment>,
          }}
          fullWidth
          defaultValue={record && record.duration}
          validate={required()}
          label={'resources.Advert.fields.duration'}
        />
        <NumberInput
          source="frequencyWeight"
          validate={freqWeightValidation}
          label="resources.Advert.fields.freqWeight"
        />
        <SelectInput
          source="status"
          choices={adStatusOptions}
          validate={required()}
          label="resources.Advert.fields.status"
        />
      </div>
      <div className={classes.extendedColumn}>
        <FormDataConsumer>
          {({ formData }) => (
            <Labeled label="resources.Advert.fields.currentImg">
              <PreviewImageField record={formData && formData.image} />
            </Labeled>
          )}
        </FormDataConsumer>

        <ImageInput
          source="adImage"
          label={'resources.Advert.fields.updateImg'}
          maxSize={maxFileSize}
          accept={allowedFileTypes}
          fullWidth
          onChange={() => setImgLoaded(false)}
          options={{
            onDropRejected: files =>
              onDropRejected({
                files,
                translate,
                notify,
                maxFileSize,
                allowedFileTypes,
              }),
            onDrop: acceptedFiles => onDrop({ acceptedFiles, setImgLoaded }),
          }}
        >
          <AspectRatioValidatingImageField
            imgLoaded={imgLoaded}
            resetImageField={resetImageField}
          />
        </ImageInput>
        <div className={classes.column}>
          {window.navigator.userAgent.indexOf('Safari') > -1 &&
          window.navigator.userAgent.indexOf('Chrome') === -1 ? (
            <ReactAdminDateInput
              source="startDate"
              label="resources.Advert.fields.liveDate"
              alwaysOn
              validate={liveDateValidation}
              options={{
                format: 'dd/MM/yyyy',
                clearable: true,
              }}
            />
          ) : (
            <DateInput
              source="startDate"
              label="resources.Advert.fields.liveDate"
              validate={liveDateValidation}
            />
          )}
          {window.navigator.userAgent.indexOf('Safari') > -1 &&
          window.navigator.userAgent.indexOf('Chrome') === -1 ? (
            <ReactAdminDateInput
              source="endDate"
              label="resources.Advert.fields.endDate"
              alwaysOn
              validate={endDateValidation}
              options={{
                format: 'dd/MM/yyyy',
                clearable: true,
              }}
            />
          ) : (
            <DateInput
              source="endDate"
              label="resources.Advert.fields.endDate"
              validate={endDateValidation}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AdvertDetail = props => {
  const classes = columnStyles();
  const notify = useNotify();
  const translate = useTranslate();
  const [imgLoaded, setImgLoaded] = useState(false); // this notifies us to rerender the ImageField as the onDrop function is synchronous

  const rest = {
    classes,
    notify,
    translate,
    urlValidation,
    freqWeightValidation,
    imgLoaded,
    setImgLoaded,
    ...props,
  };
  return <SanitizedForm {...rest} />;
};

export default AdvertDetail;
